import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, CreditCard, ArrowDownToLine, Calculator, Building2, BarChart3, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { TiplymeLogo } from "@/components/Brand";
import { InstallButton } from "@/components/InstallButton";
import { Button } from "@/components/ui/button";

const nav = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Overview", tid: "nav-overview" },
  { to: "/admin/workers", icon: Users, label: "Workers", tid: "nav-workers" },
  { to: "/admin/cards", icon: CreditCard, label: "QR / NFC Cards", tid: "nav-cards" },
  { to: "/admin/cashouts", icon: ArrowDownToLine, label: "Cashouts", tid: "nav-cashouts" },
  { to: "/admin/corporates", icon: Building2, label: "Corporates", tid: "nav-corporates" },
  { to: "/admin/reports", icon: BarChart3, label: "Reports & KPIs", tid: "nav-reports" },
  { to: "/admin/finance", icon: Calculator, label: "Finance", tid: "nav-finance", adminOnly: true },
];

const roleBadge = { admin: "bg-brand-green text-white", sales: "bg-brand-orange text-white", ops: "bg-blue-500 text-white" };

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="dark min-h-screen bg-background text-foreground">
      <div className="flex min-h-screen">
        <aside className="hidden w-64 flex-col border-r border-border/60 bg-card md:flex">
          <div className="border-b border-border/60 p-5"><TiplymeLogo dark /></div>
          <nav className="flex-1 space-y-1 p-3">
            {nav.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.tid}
                className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-brand-green text-white" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="border-t border-border/60 p-3">
            <div className="flex items-center gap-3 rounded-xl px-3 py-2">
              <div className="grid h-9 w-9 place-items-center rounded-full bg-brand-green/20 font-bold text-brand-greenLight">{user?.name?.[0] || "A"}</div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{user?.name}</p>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${roleBadge[user?.role] || "bg-secondary"}`}>{user?.role}</span>
              </div>
            </div>
            <div className="mt-1"><InstallButton variant="full" dark /></div>
            <Button variant="ghost" onClick={async () => { await logout(); navigate("/login"); }} data-testid="admin-logout-button" className="mt-1 w-full justify-start gap-2 text-muted-foreground hover:text-foreground">
              <LogOut className="h-4 w-4" /> Sign out
            </Button>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          {/* Mobile top nav */}
          <header className="flex items-center justify-between border-b border-border/60 bg-card px-5 py-3 md:hidden">
            <TiplymeLogo dark />
            <Button variant="ghost" size="icon" onClick={async () => { await logout(); navigate("/login"); }} data-testid="admin-logout-mobile"><LogOut className="h-4 w-4" /></Button>
          </header>
          <nav className="flex gap-1 overflow-x-auto border-b border-border/60 bg-card px-3 py-2 md:hidden">
            {nav.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold ${isActive ? "bg-brand-green text-white" : "text-muted-foreground"}`}>{n.label}</NavLink>
            ))}
          </nav>
          <main className="p-5 md:p-8"><Outlet /></main>
        </div>
      </div>
    </div>
  );
}
